"use client";

import { useAuth } from "@/components/auth/AuthProvider";
import { AuthSplitLayout } from "@/components/auth/AuthSplitLayout";
import { ApiError } from "@/lib/api";
import { readLinkToken, stripLinkToken } from "@/lib/link-token";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/TextInput";
import { CheckCircle2, KeyRound, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { MAX_EMAIL } from "@/lib/field-limits";

/**
 * Verrou anti-pilonnage : ce que l'écran fait du 429.
 *
 * L'API compte les demandes par adresse et sanctionne par paliers croissants
 * (30 s, 2 min, 10 min, 1 h — voir PerEmailThrottleGuard). Elle renvoie le délai
 * restant, que ce soit dans le corps ou dans `Retry-After` ; on le garde comme
 * ÉCHÉANCE et non comme durée, pour que le décompte reste juste même si l'onglet
 * dort quelques minutes entre deux battements.
 *
 * Sans délai annoncé (verrou par IP derrière un proxy qui mange l'en-tête), on
 * retombe sur le premier palier : mieux vaut rouvrir le bouton un peu tôt — la
 * tentative échouera proprement et reposera l'échéance — que le garder gris une
 * heure sans raison.
 */
const DELAI_VERROU_PAR_DEFAUT = 30;

/** « 45 s », « 2 min 05 s », « 12 min » — de quoi voir que ça descend. */
function formatRestant(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const reste = seconds % 60;
  return reste ? `${minutes} min ${String(reste).padStart(2, "0")} s` : `${minutes} min`;
}

export default function ConnexionPage() {
  const router = useRouter();
  const { requestMagicLink, loginWithToken, completeTotpLogin, loginWithCode } =
    useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // Vrai quand on arrive ici avec un jeton mort : ouvre le renvoi d'activation.
  const [expiredLink, setExpiredLink] = useState(false);
  /**
   * U-16 — jeton de lien mis de côté parce que le compte exige un second
   * facteur. Il n'a PAS été consommé par l'API : il porte encore le premier
   * facteur et ne sera brûlé qu'avec le code. Il ne quitte donc pas la mémoire de
   * cet écran — ni URL, ni stockage local.
   */
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  /**
   * Code à six chiffres reçu par email, saisi ICI plutôt que suivi par un lien.
   *
   * C'est le cœur du correctif iOS : un lien ouvre la session dans le navigateur
   * qui l'ouvre, et sur iPhone ce n'est jamais celui-ci — une PWA ajoutée à
   * l'écran d'accueil a son propre conteneur de cookies, et Mail envoie
   * systématiquement le lien dans Safari. La session se posait donc dans une
   * fenêtre que l'utilisateur n'avait pas ouverte, pendant que celle-ci restait
   * à l'écran de connexion. Un code se recopie à la main : il traverse.
   */
  const [loginCode, setLoginCode] = useState("");
  /** Le compte saisi exige un second facteur, dans le parcours PAR CODE. */
  const [codeTotpRequired, setCodeTotpRequired] = useState(false);
  /**
   * Échéance du verrou (ms epoch) et secondes restantes. Deux états et non un :
   * l'échéance est la vérité, le décompte n'est que son affichage — recalculé à
   * chaque battement depuis `Date.now()`, jamais décrémenté à l'aveugle.
   */
  const [blocageJusqua, setBlocageJusqua] = useState<number | null>(null);
  const [secondesRestantes, setSecondesRestantes] = useState(0);

  // Arrivée depuis une session coupée pour abonnement suspendu (lib/api.ts) :
  // sans cette ligne, l'utilisateur retomberait sur l'écran de connexion sans
  // savoir pourquoi, et redemanderait un lien qui serait refusé.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("suspendue") !== "1") return;
    /* eslint-disable-next-line react-hooks/set-state-in-effect */
    setError(
      "L'abonnement de votre officine est suspendu. Contactez l'administrateur de la plateforme.",
    );
  }, []);

  // Email-link entry point: the magic-link email points to /connexion#token=…
  // Detect it on mount, exchange it for a session, then go to the dashboard.
  useEffect(() => {
    const token = readLinkToken();
    if (!token) return;
    // Legitimate: entering the verifying state on mount when a token is present.
    /* eslint-disable-next-line react-hooks/set-state-in-effect */
    setVerifying(true);
    // Strip the token from the URL so a reload can't replay it.
    stripLinkToken("/connexion");
    loginWithToken(token)
      .then((res) => {
        if (res.totpRequired) {
          setPendingToken(token);
          setVerifying(false);
          return;
        }
        router.replace("/accueil");
      })
      .catch((e: unknown) => {
        // Un 403 porte une raison précise (abonnement suspendu, accès fermé) :
        // la remplacer par « lien expiré » enverrait redemander un lien qui
        // serait refusé de la même façon.
        if (e instanceof ApiError && e.status === 403) {
          setError(e.message);
          setVerifying(false);
          return;
        }
        setError("Lien de connexion invalide ou expiré. Demandez-en un nouveau.");
        // Un lien mort peut être un lien d'ACTIVATION périmé (48 h) : le champ
        // ci-dessous n'y répond pas — il envoie un lien de connexion, qui exige
        // un compte déjà activé. C'est le cas où l'écran de renvoi doit être
        // visible, sinon le retardataire n'a plus aucun chemin.
        setExpiredLink(true);
        setVerifying(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Décompte du verrou : rend le bouton au bout du délai, sans rechargement.
  useEffect(() => {
    if (blocageJusqua === null) return;
    const battement = () => {
      const restant = Math.ceil((blocageJusqua - Date.now()) / 1000);
      if (restant <= 0) {
        setBlocageJusqua(null);
        setSecondesRestantes(0);
        setError(null);
        return;
      }
      setSecondesRestantes(restant);
    };
    battement();
    const timer = setInterval(battement, 1000);
    return () => clearInterval(timer);
  }, [blocageJusqua]);

  async function submit() {
    // La touche Entrée passe par ici aussi : sans ce garde, le clavier
    // contournerait le bouton grisé et relancerait le compteur pour rien.
    if (blocageJusqua !== null) return;
    setError(null);
    setLoading(true);
    try {
      const res = await requestMagicLink(email.trim());
      // Dev: the API returns the link → extract the token and log in immediately.
      if (res.magicLink) {
        const token = res.magicLink.split("token=")[1];
        const login = await loginWithToken(token);
        if (login.totpRequired) {
          setPendingToken(token);
          return;
        }
        router.replace("/accueil");
        return;
      }
      // Prod: the link was emailed.
      posthog.capture("magic_link_requested");
      setSent(true);
    } catch (e) {
      // Trop de tentatives : le bouton se ferme jusqu'à l'échéance annoncée.
      // Le laisser cliquable ne pouvait produire qu'un second refus identique,
      // et un message figé (« patientez quelques minutes ») ne disait pas
      // combien de temps il restait vraiment à attendre.
      if (e instanceof ApiError && e.status === 429) {
        setBlocageJusqua(Date.now() + (e.retryAfter ?? DELAI_VERROU_PAR_DEFAUT) * 1000);
        setError(null);
        return;
      }
      // Le message vient de l'API et il est déjà en français (cf. lib/api.ts).
      // Le texte fixe d'avant confondait trois pannes qui ne se corrigent pas
      // du tout de la même façon — verrou anti-pilonnage, adresse refusée, API
      // éteinte — et n'en nommait qu'une seule : il envoyait relancer un
      // serveur qui tournait très bien, pendant qu'il suffisait d'attendre.
      setError(
        e instanceof Error
          ? e.message
          : "Connexion impossible. Vérifiez l'email et que l'API est démarrée.",
      );
    } finally {
      setLoading(false);
    }
  }

  /**
   * Saisie du code de connexion reçu par email (parcours sans lien).
   *
   * Le refus est volontairement indistinct : l'API répond la même chose pour un
   * code faux, un code périmé et une adresse sans code en attente. On ne cherche
   * donc pas à en dire plus que ce qu'elle dit — on rappelle seulement la
   * sortie, redemander un email, parce qu'après cinq essais manqués le code est
   * désarmé côté serveur et qu'aucune saisie ne le rouvrira.
   */
  async function submitLoginCode() {
    setError(null);
    setLoading(true);
    try {
      const res = await loginWithCode(email.trim(), loginCode.trim());
      if (res.totpRequired) {
        setCodeTotpRequired(true);
        return;
      }
      router.replace("/accueil");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Code de connexion refusé.",
      );
      setLoginCode("");
    } finally {
      setLoading(false);
    }
  }

  /**
   * Second facteur — commun aux DEUX parcours.
   *
   * Le premier facteur diffère (un jeton de lien d'un côté, l'adresse et son
   * code de l'autre) mais l'écran, lui, est le même : six chiffres à saisir. Un
   * seul bloc de rendu et un seul état `code`, sinon la même demande vivrait en
   * double et les deux copies divergeraient à la première retouche.
   */
  async function submitTotp() {
    setError(null);
    setLoading(true);
    try {
      if (pendingToken) {
        await completeTotpLogin(pendingToken, code.trim());
      } else {
        await loginWithCode(email.trim(), loginCode.trim(), code.trim());
      }
      router.replace("/accueil");
    } catch (e) {
      // Le message vient de l'API : il ne distingue pas un code faux d'un code
      // déjà utilisé, et c'est voulu.
      setError(
        e instanceof Error ? e.message : "Code de vérification refusé.",
      );
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  /**
   * Retour à la case départ pour se faire renvoyer un email.
   *
   * Remet à zéro TOUT ce que portait la tentative précédente : l'ancien code est
   * de toute façon périmé côté serveur dès qu'un nouveau lien part, et laisser
   * six chiffres morts dans le champ ne pourrait que faire brûler un essai du
   * code suivant.
   */
  function redemander() {
    setSent(false);
    setLoginCode("");
    setCodeTotpRequired(false);
    setCode("");
    setError(null);
  }

  return (
    <AuthSplitLayout>
      <div>
        {/* L'en-tête de marque disparaît au-delà de lg : le panneau de gauche
            porte déjà le logo et le nom, les répéter à 30 cm d'intervalle
            donnerait deux fois la même chose sur la même ligne d'horizon. */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center lg:hidden">
          {/* Le logo est détouré : il se pose directement sur le fond de page,
              qui change avec le thème. `alt` vide car le titre juste dessous dit
              déjà « PharmacoWork » — le doubler le ferait annoncer deux fois.
              Balise <img> et non next/image : à 56 px sur un fichier local,
              l'optimiseur n'a rien à optimiser, et le dépôt n'utilise
              next/image nulle part. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icons/logo-mark.png" alt="" width={56} height={56} className="h-14 w-14" />
          <h1 className="text-[22px] font-bold text-text">PharmacoWork</h1>
          <p className="text-[14px] text-text-faint">
            L&apos;espace de travail de votre officine
          </p>
        </div>

        {/* Titre propre à la colonne de droite sur grand écran : le panneau
            présente le produit, celui-ci annonce l'action attendue. */}
        <div className="mb-6 hidden lg:block">
          <h1 className="text-[26px] font-bold leading-tight text-text">
            Se connecter
          </h1>
          <p className="mt-1.5 text-[14px] text-text-faint">
            Saisissez votre email : nous vous envoyons un lien de connexion.
          </p>
        </div>

        <div className="rounded-card border border-border bg-surface p-5 shadow-card">
          {verifying ? (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <Loader2 size={28} className="animate-spin text-primary" />
              <p className="text-[15px] font-semibold text-text">Connexion…</p>
              <p className="text-[13px] text-text-faint">
                Vérification de votre lien de connexion.
              </p>
            </div>
          ) : pendingToken || codeTotpRequired ? (
            /* Second temps de la connexion : le premier facteur seul — lien
               cliqué OU code recopié — n'a rien ouvert. */
            <div className="flex flex-col gap-3">
              <div className="flex flex-col items-center gap-2 text-center">
                <ShieldCheck size={28} className="text-primary" />
                <p className="text-[15px] font-semibold text-text">
                  Vérification en deux étapes
                </p>
                <p className="text-[13px] text-text-faint">
                  Saisissez le code à 6 chiffres de votre application
                  d&apos;authentification.
                </p>
              </div>
              <TextInput
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitTotp();
                }}
                inputMode="text"
                autoComplete="one-time-code"
                maxLength={20}
                placeholder="123 456"
                icon={<KeyRound size={18} strokeWidth={2} />}
                className="tracking-[0.2em]"
              />
              {error ? (
                <p className="text-[12px] text-danger-deep">{error}</p>
              ) : null}
              <Button
                fullWidth
                disabled={loading || code.trim().length < 6}
                onClick={submitTotp}
              >
                {loading ? "Vérification…" : "Se connecter"}
              </Button>
              <p className="text-center text-[12px] text-text-faint">
                Téléphone indisponible ? Utilisez l&apos;un de vos codes de secours.
              </p>
            </div>
          ) : sent ? (
            /* L'écran n'annonce plus seulement qu'un email est parti : il ATTEND.
               C'est ce qui répare la connexion sur iPhone — tant que la seule
               voie était le lien, la session s'ouvrait dans le navigateur qui
               l'ouvrait (Safari, toujours), et cette fenêtre-ci restait à la
               porte sans jamais rien pouvoir y faire. Le code, lui, revient ici. */
            <div className="flex flex-col gap-3">
              <div className="flex flex-col items-center gap-2 text-center">
                <CheckCircle2 size={28} className="text-primary" />
                <p className="text-[15px] font-semibold text-text">Email envoyé</p>
                <p className="text-[13px] text-text-faint">
                  Saisissez ici le code à 6 chiffres qu&apos;il contient.
                </p>
              </div>
              <TextInput
                value={loginCode}
                onChange={(e) => setLoginCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitLoginCode();
                }}
                /* `numeric` et non `text` : sur mobile, c'est la différence entre
                   un pavé de chiffres et un clavier complet pour taper six
                   chiffres. `one-time-code` laisse iOS proposer le code lu dans
                   l'email en suggestion au-dessus du clavier. */
                inputMode="numeric"
                autoComplete="one-time-code"
                /* 7 et non 6 : « 123 456 » se recopie avec son espace, que l'API
                   retire avant comparaison. Couper à 6 tronquerait la saisie de
                   quiconque reproduit ce qu'il lit. */
                maxLength={7}
                placeholder="123 456"
                icon={<KeyRound size={18} strokeWidth={2} />}
                className="tracking-[0.2em]"
              />
              {error ? (
                <p className="text-[12px] text-danger-deep">{error}</p>
              ) : null}
              <Button
                fullWidth
                disabled={
                  loading || loginCode.replace(/\D/g, "").length < 6
                }
                onClick={submitLoginCode}
              >
                {loading ? "Connexion…" : "Se connecter"}
              </Button>
              <p className="text-center text-[12px] text-text-faint">
                L&apos;email contient aussi un lien : il vous connecte
                directement si vous relevez votre courrier sur cet appareil.
              </p>
              <button
                type="button"
                onClick={redemander}
                className="text-center text-[12px] font-semibold text-primary"
              >
                Rien reçu ? Renvoyer un email
              </button>
            </div>
          ) : (
            <>
              <label className="mb-2 block text-[13px] font-medium text-text-muted">
                Email professionnel
              </label>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
                maxLength={MAX_EMAIL}
                placeholder="vous@officine.fr"
                icon={<Mail size={18} strokeWidth={2} />}
              />
              {error ? (
                <p className="mt-2 text-[12px] text-danger-deep">{error}</p>
              ) : null}
              {blocageJusqua !== null ? (
                /* `role="alert"` : le message apparaît sans que rien ne bouge à
                   l'écran, un lecteur d'écran ne le verrait pas passer. */
                <p role="alert" className="mt-2 text-[12px] text-danger-deep">
                  Trop de tentatives de connexion. Réessayez dans{" "}
                  {formatRestant(secondesRestantes)}.
                </p>
              ) : null}
              {expiredLink ? (
                <p className="mt-2 text-[12px] text-text-faint">
                  Votre compte n&apos;est pas encore activé ?{" "}
                  <Link
                    href="/activation"
                    className="font-semibold text-primary"
                  >
                    Recevoir un nouveau lien d&apos;activation
                  </Link>
                </p>
              ) : null}
              <Button
                fullWidth
                className="mt-4"
                /* Grisé par les styles `disabled:` du bouton pendant tout le
                   verrou : le libellé dit l'attente, l'état dit qu'elle court. */
                disabled={loading || !email.trim() || blocageJusqua !== null}
                onClick={submit}
              >
                {blocageJusqua !== null
                  ? `Réessayez dans ${formatRestant(secondesRestantes)}`
                  : loading
                    ? "Connexion…"
                    : "Recevoir le lien de connexion"}
              </Button>
              <div className="mt-4 flex items-center justify-center gap-1.5 text-[12px] text-text-faint">
                <Lock size={13} strokeWidth={2} />
                <span>Connexion sécurisée sans mot de passe</span>
              </div>
              <p className="mt-4 border-t border-border pt-4 text-center text-[13px] text-text-faint">
                Nouvelle officine ?{" "}
                <Link href="/inscription" className="font-semibold text-primary">
                  Demander une inscription
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </AuthSplitLayout>
  );
}
